import React, { useCallback, useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { spriteUrl, featuredCharacter, SEL_SKILLS, BACKDROPS } from "./gameUtils";
import { Sprite, SparkleBurst, CharacterView, SpeakerIcon, BackIcon, ConfettiRain } from "./fx";
import DragToTarget from "./DragToTarget";
import TapSequence from "./TapSequence";
import ChooseKind from "./ChooseKind";
import BreatheTrace from "./BreatheTrace";
import WaterGarden from "./WaterGarden";
import LeafPile from "./LeafPile";
import FeelingsMatch from "./FeelingsMatch";
import CheerMeter from "./CheerMeter";
import PetSoothe from "./PetSoothe";
import ShelveBooks from "./ShelveBooks";
import CalmAlarm from "./CalmAlarm";
import BeeStill from "./BeeStill";
import WaitTurn from "./WaitTurn";
import SplitShare from "./SplitShare";
import HonestReturn from "./HonestReturn";
import Tiptoe from "./Tiptoe";
import Carry from "./Carry";

// Full-screen container for one helper moment: an intro card that reads the
// setup aloud, the interaction itself on a painted backdrop, and a shared
// completion beat that speaks the SEL takeaway.

const ActivityView = ({ scenario, sounds, onComplete, onClose }) => {
  const [phase, setPhase] = useState("intro");
  const [doneBurst, setDoneBurst] = useState(0);
  const character = featuredCharacter(scenario);
  const skill = SEL_SKILLS[scenario.skill];
  const template = scenario.template;

  useEffect(() => {
    if (phase === "intro") sounds.speak(scenario.introLine);
    if (phase === "done") {
      sounds.speak(scenario.takeawayLine);
      const id = window.setTimeout(() => setDoneBurst((b) => b + 1), 250);
      return () => window.clearTimeout(id);
    }
    return undefined;
  }, [phase, scenario.introLine, scenario.takeawayLine, sounds]);

  const finishActivity = useCallback(() => {
    sounds.playPop(0.9);
    setPhase("done");
  }, [sounds]);

  const showPromptInContainer = template.type === "dragToTarget" || template.type === "tapSequence";

  const renderTemplate = () => {
    switch (template.type) {
      case "dragToTarget":
        return <DragToTarget payload={template} sounds={sounds} onDone={finishActivity} />;
      case "tapSequence":
        return <TapSequence payload={template} sounds={sounds} onDone={finishActivity} />;
      case "chooseKind":
        return (
          <ChooseKind
            payload={template}
            sounds={sounds}
            onDone={finishActivity}
            prompt={template.prompt}
            speakPrompt={() => sounds.speak(template.prompt)}
          />
        );
      case "breatheTrace":
        return (
          <BreatheTrace
            payload={template}
            sounds={sounds}
            onDone={finishActivity}
            prompt={template.prompt}
            speakPrompt={() => sounds.speak(template.prompt)}
          />
        );
      case "waterGarden":
        return <WaterGarden payload={template} sounds={sounds} onDone={finishActivity} />;
      case "leafPile":
        return <LeafPile payload={template} sounds={sounds} onDone={finishActivity} />;
      case "feelingsMatch":
        return <FeelingsMatch payload={template} sounds={sounds} onDone={finishActivity} />;
      case "cheerMeter":
        return <CheerMeter payload={template} sounds={sounds} onDone={finishActivity} />;
      case "petSoothe":
        return <PetSoothe payload={template} sounds={sounds} onDone={finishActivity} />;
      case "shelveBooks":
        return <ShelveBooks payload={template} sounds={sounds} onDone={finishActivity} />;
      case "calmAlarm":
        return <CalmAlarm payload={template} sounds={sounds} onDone={finishActivity} />;
      case "beeStill":
        return <BeeStill payload={template} sounds={sounds} onDone={finishActivity} />;
      case "waitTurn":
        return <WaitTurn payload={template} sounds={sounds} onDone={finishActivity} />;
      case "splitShare":
        return <SplitShare payload={template} sounds={sounds} onDone={finishActivity} />;
      case "honestReturn":
        return <HonestReturn payload={template} sounds={sounds} onDone={finishActivity} />;
      case "tiptoe":
        return <Tiptoe payload={template} sounds={sounds} onDone={finishActivity} />;
      case "carry":
        return <Carry payload={template} sounds={sounds} onDone={finishActivity} />;
      default:
        return null;
    }
  };

  return (
    <motion.div
      className="ch-activity"
      initial={{ opacity: 0, scale: 1.06 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 1.06 }}
      transition={{ duration: 0.3 }}
    >
      <div
        className="ch-activity-backdrop"
        style={{ backgroundImage: `url(${spriteUrl(BACKDROPS[scenario.backdrop])})` }}
      />
      <div className="ch-activity-wash" />

      <header className="ch-activity-bar">
        <button
          type="button"
          className="ch-icon-btn"
          onClick={() => {
            sounds.stop();
            onClose();
          }}
          aria-label="Back to the city"
        >
          <BackIcon />
        </button>
        <div className="ch-activity-titles">
          <h2>{scenario.title}</h2>
          {phase !== "playing" && <span>{skill.label}</span>}
        </div>
        <div className="ch-icon-btn ch-icon-btn--ghost" aria-hidden="true" />
      </header>

      <AnimatePresence mode="wait">
        {phase === "intro" && (
          <motion.div
            key="intro"
            className="ch-intro"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            {character ? (
              <motion.div
                className="ch-intro-stage"
                initial={{ scale: 0.85, y: 20, opacity: 0 }}
                animate={{ scale: 1, y: 0, opacity: 1 }}
                transition={{ type: "spring", stiffness: 200, damping: 18 }}
              >
                <CharacterView character={character} happy={false} />
              </motion.div>
            ) : (
              <motion.div
                className="ch-intro-icon"
                initial={{ scale: 0.7, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ type: "spring", stiffness: 200, damping: 16 }}
              >
                <Sprite name={scenario.iconAsset} />
              </motion.div>
            )}

            <motion.div
              className="ch-intro-card"
              initial={{ y: 24, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.1 }}
            >
              <span className="ch-intro-chip">{skill.label}</span>
              <div className="ch-intro-line">
                <p>{scenario.introLine}</p>
                <button
                  type="button"
                  className="ch-speaker"
                  onClick={() => sounds.speak(scenario.introLine)}
                  aria-label="Read aloud"
                >
                  <SpeakerIcon size={18} />
                </button>
              </div>
              <button
                type="button"
                className="ch-primary-btn"
                onClick={() => {
                  sounds.stop();
                  setPhase("playing");
                }}
              >
                Let&apos;s help!
              </button>
            </motion.div>
          </motion.div>
        )}

        {phase === "playing" && (
          <motion.div
            key="playing"
            className="ch-playing"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            {showPromptInContainer && (
              <div className="ch-prompt-capsule">
                <span>{template.prompt}</span>
                <button
                  type="button"
                  className="ch-speaker"
                  onClick={() => sounds.speak(template.prompt)}
                  aria-label="Read aloud"
                >
                  <SpeakerIcon size={18} />
                </button>
              </div>
            )}
            {renderTemplate()}
          </motion.div>
        )}

        {phase === "done" && (
          <motion.div
            key="done"
            className="ch-done"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <ConfettiRain trigger={doneBurst} />

            <motion.div
              className="ch-done-stage"
              initial={{ scale: 0.5, rotate: -8, opacity: 0 }}
              animate={{ scale: 1, rotate: 0, opacity: 1 }}
              transition={{ type: "spring", stiffness: 230, damping: 14 }}
            >
              {character ? (
                <CharacterView character={character} happy />
              ) : (
                <div className="ch-medallion">
                  <Sprite name={scenario.iconAsset} />
                </div>
              )}
              <SparkleBurst trigger={doneBurst} reach={120} style={{ left: "50%", top: "40%" }} />
            </motion.div>

            <motion.h3
              className="ch-done-title"
              initial={{ scale: 0.4, y: 14, opacity: 0 }}
              animate={{ scale: 1, y: 0, opacity: 1 }}
              transition={{ type: "spring", stiffness: 300, damping: 15, delay: 0.12 }}
            >
              You did it!
            </motion.h3>

            <div className="ch-intro-line ch-done-line">
              <p>{scenario.takeawayLine}</p>
              <button
                type="button"
                className="ch-speaker"
                onClick={() => sounds.speak(scenario.takeawayLine)}
                aria-label="Read aloud"
              >
                <SpeakerIcon size={18} />
              </button>
            </div>

            <div className="ch-skill-badge">
              {skill.label} +1
            </div>

            <button
              type="button"
              className="ch-primary-btn"
              onClick={() => {
                sounds.stop();
                onComplete();
              }}
            >
              Hooray!
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default ActivityView;
